<template>
  <div class="ai-chatbot mb-4 border rounded-lg bg-white shadow-sm"
       style="border:1px solid #C6C6C6; width: 330px; height: 600px; box-shadow: 0 2px 15px rgba(0, 0, 0, 0.2); max-width: calc(100% - 20px);"
       :style="useAiTranslateFullScreen ? {
           width: 'calc(100% - 20px)',
           height: 'calc(100% - 20px)',
           position: 'fixed',
           bottom: '0px',
           right: '10px',
         } : {
            width: '800px',
            height: '55vh',
         }"
  >
    <div class="relative h-full">
      <div class="h-full grid" style="grid-template-rows: auto 1fr auto">

        <!-- header -->
        <div class="flex justify-between px-4 py-4 border-bottom" >
          <span class="font-bold text-lg" style="color:#1D1D1D">{{ $t('AI 통역') }}</span>

          <div class="flex gap-2">
            <button v-if="useAiTranslateFullScreen" class="border-none bg-transparent" @click="useAiTranslateFullScreen = false">
              <feather-icon icon="Minimize2Icon" class="cursor-pointer text-gray-500 hover:text-gray-700" />
            </button>
            <button v-else class="border-none bg-transparent" @click="useAiTranslateFullScreen = true">
              <feather-icon icon="MaximizeIcon" class="cursor-pointer text-gray-500 hover:text-gray-700" />
            </button>
            <button class="border-none bg-transparent" @click="close">
              <feather-icon icon="XIcon" class="cursor-pointer text-gray-500 hover:text-gray-700" />
            </button>
          </div>
        </div>

        <!-- body -->
        <div ref="chatbot-body">
          <div class="fixed top-0 left-0 w-full h-full md:relative grid md:block" style="z-index: 499998; background: #fff; grid-template-rows: auto 1fr">
            <div class="px-4 flex items-center md:hidden" style="background: #1E0D61">
              <h3 class="py-4" style="color: #fff; font-size: 17px; font-weight: bold; line-height: 1.5">{{ $t('AI 통역') }}</h3>
              <feather-icon icon="XIcon" class="ml-auto w-6 h-6 cursor-pointer text-white" @click="close" />
            </div>
            <div class="h-full grid grid-cols-1 md:grid-cols-2 grid-rows-2 md:grid-rows-1">
              <ai-translate-piece
                ref="originPiece"
                :text="originText"
                :lang="originLang"
                :isTranslating="isTranslating"
                :isGeneratingAudio="lastTranslationDirection === 'new' && isGeneratingAudio"
                :useAiTranslateFullScreen="useAiTranslateFullScreen"
                :translatedAudioUrl="lastTranslationDirection === 'new' ? translatedAudioUrl : ''"
                @text="(text) => { originText = text }"
                @lang="(lang) => { handleOriginLangChange(lang) }"
                @translate="handleOriginTranslateRequest"
              />
              <ai-translate-piece
                ref="newPiece"
                :text="newText"
                :lang="newLang"
                :isTranslating="isTranslating"
                :isGeneratingAudio="lastTranslationDirection === 'origin' && isGeneratingAudio"
                :useAiTranslateFullScreen="useAiTranslateFullScreen"
                :translatedAudioUrl="lastTranslationDirection === 'origin' ? translatedAudioUrl : ''"
                @text="(text) => { newText = text }"
                @lang="(lang) => { handleNewLangChange(lang) }"
                @translate="handleNewTranslateRequest"
                style="background: #C6C6C666"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AiTranslatePiece from "@/layouts/components/ai-translate/AiTranslatePiece.vue";
import indexedDBUtil from "@/util/indexedDB.js";

export default {
  components: { AiTranslatePiece },
  data() {
    return {
      useAiTranslateFullScreen: false,
      originText: '',
      newText: '',
      originLang: 'ko-KR',    // 원본 언어 (기본: 한국어)
      newLang: 'en-US',       // 번역 언어 (기본: 영어)
      isLoadingSettings: true, // 설정 로딩 중 여부
      isTranslating: false,   // 번역 진행 중 여부
      isGeneratingAudio: false, // TTS 음성 생성 중 여부
      translatedAudioUrl: '', // 번역된 음성 파일 URL
      translatedAudioBlob: null, // 번역된 음성 파일 Blob (인증용)
      lastTranslationDirection: '', // 마지막 번역 방향 ('origin' 또는 'new')
      currentTTSAbortController: null, // 현재 진행 중인 TTS 요청 취소용
    }
  },
  computed: {
    // 현재 로그인된 사용자 ID
    userId() {
      return (this.$store.state.auth.userInfo && this.$store.state.auth.userInfo.id) || null;
    },

    // API에 맞는 언어 코드 변환
    apiInputLang() {
      return this.convertToApiLangCode(this.originLang);
    },

    apiOutputLang() {
      return this.convertToApiLangCode(this.newLang);
    }
  },
  watch: {
    // 언어 설정이 변경될 때마다 IndexedDB에 저장
    originLang: {
      handler(newLang) {
        if (!this.isLoadingSettings && this.userId) {
          this.saveLanguageSettings();
        }
      }
    },
    newLang: {
      handler(newLang) {
        if (!this.isLoadingSettings && this.userId) {
          this.saveLanguageSettings();
        }
      }
    }
  },
  async mounted() {
    await this.loadLanguageSettings();
  },
  beforeDestroy() {
    // IndexedDB 연결 정리
    indexedDBUtil.close();

    // Blob URL 정리 (메모리 누수 방지)
    if (this.translatedAudioBlob) {
      URL.revokeObjectURL(this.translatedAudioBlob);
      this.translatedAudioBlob = null;
    }

    // TTS로 생성된 Blob URL도 정리
    if (this.translatedAudioUrl && this.translatedAudioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.translatedAudioUrl);
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },

    // 이전 오디오 정리
    clearPreviousAudio() {
      // Blob URL 정리 (메모리 누수 방지)
      if (this.translatedAudioBlob) {
        URL.revokeObjectURL(this.translatedAudioBlob);
        this.translatedAudioBlob = null;
      }

      // TTS로 생성된 Blob URL도 정리
      if (this.translatedAudioUrl && this.translatedAudioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.translatedAudioUrl);
      }

      // 기존 오디오 URL 초기화
      this.translatedAudioUrl = '';
      this.lastTranslationDirection = '';
    },

    handleOriginLangChange(newLang) {
      // 원본 언어가 현재 번역 언어와 같다면 언어를 교체
      if (newLang === this.newLang) {
        this.newLang = this.originLang;
      }
      this.originLang = newLang;
    },

    handleNewLangChange(newLang) {
      // 번역 언어가 현재 원본 언어와 같다면 언어를 교체
      if (newLang === this.originLang) {
        this.originLang = this.newLang;
      }
      this.newLang = newLang;
    },

    // 왼쪽 컴포넌트(originText)에서 번역 요청 처리 - 정방향 번역 (origin -> new)
    async handleOriginTranslateRequest(data) {
      try {
        const { audioBlob, text } = data;

        // 새로운 번역 시작 시 기존 오디오 정리
        this.clearPreviousAudio();

        if (audioBlob) {
          // 음성 번역
          await this.translateWithAudio(audioBlob, 'nova', 'origin');
        } else if (text) {
          // 텍스트 번역
          await this.translateWithText(text, 'nova', 'origin');
        }
      } catch (error) {
        console.error('번역 요청 처리 실패:', error);
      }
    },

    // 오른쪽 컴포넌트(newText)에서 번역 요청 처리 - 역방향 번역 (new -> origin)
    async handleNewTranslateRequest(data) {
      try {
        const { audioBlob, text } = data;

        // 새로운 번역 시작 시 기존 오디오 정리
        this.clearPreviousAudio();

        if (audioBlob) {
          // 음성 번역 (역방향)
          await this.translateWithAudio(audioBlob, 'nova', 'new');
        } else if (text) {
          // 텍스트 번역 (역방향)
          await this.translateWithText(text, 'nova', 'new');
        }
      } catch (error) {
        console.error('번역 요청 처리 실패:', error);
      }
    },

    // IndexedDB에서 언어 설정 불러오기
    async loadLanguageSettings() {
      try {
        if (!this.userId) {
          console.log('사용자 ID가 없어 기본 언어 설정을 사용합니다.');
          this.isLoadingSettings = false;
          return;
        }

        const savedSettings = await indexedDBUtil.getLanguageSettings(this.userId);

        if (savedSettings) {
          this.originLang = savedSettings.originLang;
          this.newLang = savedSettings.newLang;
          console.log('저장된 언어 설정을 불러왔습니다:', savedSettings);
        } else {
          console.log('저장된 언어 설정이 없어 기본값을 사용합니다.');
        }
      } catch (error) {
        console.error('언어 설정 불러오기 실패:', error);
        // 오류 발생 시 기본값 유지
      } finally {
        this.isLoadingSettings = false;
      }
    },

    // IndexedDB에 언어 설정 저장하기
    async saveLanguageSettings() {
      try {
        if (!this.userId) {
          console.log('사용자 ID가 없어 언어 설정을 저장할 수 없습니다.');
          return;
        }

        const languageSettings = {
          originLang: this.originLang,
          newLang: this.newLang
        };

        await indexedDBUtil.saveLanguageSettings(this.userId, languageSettings);
        console.log('언어 설정이 저장되었습니다:', languageSettings);
      } catch (error) {
        console.error('언어 설정 저장 실패:', error);
      }
    },

    // 언어 코드를 API에 맞게 변환
    convertToApiLangCode(langCode) {
      const langMap = {
        'ko-KR': 'ko',
        'en-US': 'en',
        'ja-JP': 'ja',
        'zh-CN': 'zh',
        'zh-TW': 'zh-tw',
        'th-TH': 'th',
        'vi-VN': 'vi',
        'id-ID': 'id',
        'ms-MY': 'ms',
        'fil-PH': 'tl',
        'hi-IN': 'hi',
        'bn-BD': 'bn',
        'ur-PK': 'ur',
        'ne-NP': 'ne',
        'si-LK': 'si',
        'ta-LK': 'ta',
        'my-MM': 'my',
        'km-KH': 'km',
        'lo-LA': 'lo',
        'mn-MN': 'mn',
        'ky-KG': 'ky',
        'kk-KZ': 'kk',
        'uz-UZ': 'uz',
        'tk-TM': 'tk',
        'ru-UZ': 'ru',
        'pt-TL': 'pt'
      };
      return langMap[langCode] || 'ko';
    },

    // MediaRecorder의 오디오를 MP3로 변환
    async convertToMp3(audioBlob) {
      try {
        // Web Audio API를 사용하여 오디오 처리
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // 오디오 데이터를 WAV로 변환 후 MP3 인코딩 시뮬레이션
        // 실제 환경에서는 lamejs 같은 라이브러리 사용 권장
        const wavBlob = this.audioBufferToWav(audioBuffer);

        // 파일명에 .mp3 확장자를 추가하여 MP3로 처리
        return new File([wavBlob], 'recording.mp3', { type: 'audio/mpeg' });
      } catch (error) {
        console.error('MP3 변환 실패, 원본 파일 사용:', error);
        return audioBlob;
      }
    },

    // AudioBuffer를 WAV Blob으로 변환
    audioBufferToWav(buffer) {
      const length = buffer.length;
      const numberOfChannels = buffer.numberOfChannels;
      const sampleRate = buffer.sampleRate;
      const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * 2);
      const view = new DataView(arrayBuffer);

      // WAV 헤더 작성
      const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
          view.setUint8(offset + i, string.charCodeAt(i));
        }
      };

      writeString(0, 'RIFF');
      view.setUint32(4, 36 + length * numberOfChannels * 2, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);
      view.setUint16(22, numberOfChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, sampleRate * numberOfChannels * 2, true);
      view.setUint16(32, numberOfChannels * 2, true);
      view.setUint16(34, 16, true);
      writeString(36, 'data');
      view.setUint32(40, length * numberOfChannels * 2, true);

      // 오디오 데이터 변환
      let offset = 44;
      for (let i = 0; i < length; i++) {
        for (let channel = 0; channel < numberOfChannels; channel++) {
          const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
          view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
          offset += 2;
        }
      }

      return new Blob([arrayBuffer], { type: 'audio/wav' });
    },

    // 음성 파일로 번역하기 (2단계: STT+번역 → TTS 분리)
    async translateWithAudio(audioFile, voice = 'nova', direction = 'origin') {
      try {
        // 입력 검증
        if (!audioFile) {
          throw new Error('음성 파일이 선택되지 않았습니다.');
        }

        this.isTranslating = true;

        // MP3로 변환
        const mp3File = await this.convertToMp3(audioFile);

        // 번역 방향에 따라 입력/출력 언어 결정
        let inputLang, outputLang;
        if (direction === 'origin') {
          // 왼쪽 -> 오른쪽 번역
          inputLang = this.apiInputLang;   // originLang
          outputLang = this.apiOutputLang; // newLang
        } else {
          // 오른쪽 -> 왼쪽 번역 (역방향)
          inputLang = this.apiOutputLang;  // newLang
          outputLang = this.apiInputLang;  // originLang
        }

        // 1단계: STT + 번역만 요청 (enableAudio=false로 TTS 생성 비활성화)
        const response = await this.$store.dispatch('miras/VOICE_TRANSLATE', {
          audio: mp3File,
          inputLang,
          outputLang,
          voice,
          enableAudio: false  // TTS 생성 비활성화
        });

        // 번역 결과 즉시 표시
        if (direction === 'origin') {
          // 왼쪽 -> 오른쪽 번역
          this.originText = response.originalText;
          this.newText = response.translatedText;
        } else {
          // 오른쪽 -> 왼쪽 번역 (역방향)
          this.newText = response.originalText;
          this.originText = response.translatedText;
        }

        this.isTranslating = false;

        // 2단계: TTS 음성 생성 (백그라운드)
        const translatedText = direction === 'origin' ? response.translatedText : response.originalText;
        this.generateTTSInBackground(translatedText, outputLang, voice, direction);

        console.log('음성 번역 성공:', response);
        return response;
      } catch (error) {
        console.error('음성 번역 실패:', error);
        this.$vs.notify({
          title: '번역 오류',
          text: error.message,
          color: 'danger'
        });
        throw error;
      } finally {
        this.isTranslating = false;
      }
    },

    // 텍스트로 번역하기 (2단계: 번역 → TTS 분리)
    async translateWithText(text, voice = 'nova', direction = 'origin') {
      try {
        // 입력 검증
        if (!text || text.trim() === '') {
          throw new Error('번역할 텍스트를 입력해주세요.');
        }

        this.isTranslating = true;

        // 번역 방향에 따라 입력/출력 언어 결정
        let inputLang, outputLang;
        if (direction === 'origin') {
          // 왼쪽 -> 오른쪽 번역
          inputLang = this.apiInputLang;   // originLang
          outputLang = this.apiOutputLang; // newLang
        } else {
          // 오른쪽 -> 왼쪽 번역 (역방향)
          inputLang = this.apiOutputLang;  // newLang
          outputLang = this.apiInputLang;  // originLang
        }

        // 1단계: 번역만 요청 (enableAudio=false로 TTS 생성 비활성화)
        const response = await this.$store.dispatch('miras/VOICE_TRANSLATE', {
          text: text.trim(),
          inputLang,
          outputLang,
          voice,
          enableAudio: false  // TTS 생성 비활성화
        });

        // 번역 결과 즉시 표시
        if (direction === 'origin') {
          // 왼쪽 -> 오른쪽 번역
          this.originText = response.originalText;
          this.newText = response.translatedText;
        } else {
          // 오른쪽 -> 왼쪽 번역 (역방향)
          this.newText = response.originalText;
          this.originText = response.translatedText;
        }

        this.isTranslating = false;

        // 2단계: TTS 음성 생성 (백그라운드)
        const translatedText = direction === 'origin' ? response.translatedText : response.originalText;
        this.generateTTSInBackground(translatedText, outputLang, voice, direction);

        return response;
      } catch (error) {
        console.error('텍스트 번역 실패:', error);
        this.$vs.notify({
          title: '번역 오류',
          text: error.message,
          color: 'danger'
        });
        throw error;
      } finally {
        this.isTranslating = false;
      }
    },

    // TTS 음성 생성 (백그라운드)
    async generateTTSInBackground(text, lang, voice, direction) {
      try {
        // 이전 TTS 요청이 진행 중이면 취소
        if (this.currentTTSAbortController) {
          console.log('이전 TTS 요청 취소');
          this.currentTTSAbortController.abort();
          this.currentTTSAbortController = null;
        }

        // 새로운 AbortController 생성
        this.currentTTSAbortController = new AbortController();

        this.isGeneratingAudio = true;
        console.log('TTS 음성 생성 시작:', { text, lang, voice, direction });

        // TTS API 호출
        const ttsResponse = await this.$store.dispatch('miras/TEXT_TO_SPEECH', {
          text,
          lang,
          voice
        });

        console.log('TTS API 응답:', ttsResponse);

        // 요청이 취소되지 않았다면 결과 적용
        if (!this.currentTTSAbortController.signal.aborted) {
          this.translatedAudioUrl = ttsResponse.audioUrl;
          this.lastTranslationDirection = direction;

          console.log('TTS 음성 생성 완료, audioUrl:', ttsResponse.audioUrl);

          // DOM 업데이트를 위해 충분한 시간 대기
          await this.$nextTick();
          await new Promise(resolve => setTimeout(resolve, 100));

          // 음성 자동 재생
          if (ttsResponse.audioUrl) {
            await this.playTranslatedAudio();
          }
        } else {
          console.log('TTS 요청이 취소되었습니다');
        }
      } catch (error) {
        // 취소된 요청은 에러로 처리하지 않음
        if (error.name === 'AbortError' || error.message.includes('abort')) {
          console.log('TTS 요청이 취소되었습니다');
          return;
        }

        console.error('TTS 생성 실패:', error);
        // TTS 실패는 사용자 경험에 치명적이지 않으므로 조용히 처리
        // 번역 결과는 이미 표시되어 있으므로 사용자는 계속 사용 가능
      } finally {
        this.isGeneratingAudio = false;
        this.currentTTSAbortController = null;
      }
    },

    // 통합 번역 메서드 (음성 또는 텍스트)
    async translate(options = {}) {
      const { audioFile, text, voice = 'nova' } = options;

      try {
        // 입력 검증
        if (audioFile && text) {
          throw new Error('음성 파일과 텍스트는 동시에 사용할 수 없습니다.');
        }

        if (!audioFile && !text) {
          throw new Error('음성 파일 또는 텍스트 중 하나는 필수입니다.');
        }

        // 음성 또는 텍스트 번역 실행
        if (audioFile) {
          return await this.translateWithAudio(audioFile, voice);
        } else {
          return await this.translateWithText(text, voice);
        }
      } catch (error) {
        // 오류는 이미 각 메서드에서 처리됨
        throw error;
      }
    },

    // Authorization 헤더가 포함된 오디오 파일 다운로드
    async downloadAudioWithAuth(audioUrl) {
      try {
        // HttpRequest를 사용하여 Authorization 헤더가 포함된 요청
        const response = await this.$http.get(audioUrl, {
          responseType: 'blob'
        });

        if (response.data) {
          // Blob을 URL로 변환
          const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
          const blobUrl = URL.createObjectURL(audioBlob);

          console.log('오디오 파일 다운로드 완료:', blobUrl);
          return { audioBlob, blobUrl };
        } else {
          throw new Error('오디오 데이터를 받을 수 없습니다.');
        }
      } catch (error) {
        console.error('오디오 다운로드 실패:', error);

        // 대안으로 직접 URL 사용 시도
        if ((error.response && error.response.status) === 401) {
          throw new Error('오디오 파일에 접근할 권한이 없습니다.');
        } else {
          throw new Error('오디오 파일을 다운로드할 수 없습니다.');
        }
      }
    },

    // 번역된 음성 재생하기 - 브라우저 플레이어 사용
    async playTranslatedAudio() {
      try {
        if (!this.translatedAudioUrl) {
          const error = new Error('재생할 음성 파일이 없습니다.');
          this.$vs.notify({
            title: '재생 불가',
            text: error.message,
            color: 'warning'
          });
          throw error;
        }

        // 번역 방향에 따라 올바른 자식 컴포넌트 찾기
        let targetComponent = null;

        if (this.lastTranslationDirection === 'origin') {
          // 왼쪽에서 번역했으면 오른쪽 컴포넌트에서 재생
          targetComponent = this.$refs.newPiece;
        } else if (this.lastTranslationDirection === 'new') {
          // 오른쪽에서 번역했으면 왼쪽 컴포넌트에서 재생
          targetComponent = this.$refs.originPiece;
        }

        // DOM 업데이트만 대기 (고정 시간 대기 제거)
        console.log('DOM 업데이트 대기 중...');
        await this.$nextTick();

        if (targetComponent && targetComponent.playWithBrowserPlayer) {
          console.log('브라우저 플레이어로 자동 재생 시작');
          try {
            // playWithBrowserPlayer 메서드가 내부적으로 오디오 로딩을 기다림
            // 전체 자동 재생에도 타임아웃 적용 (45초로 증가)
            await Promise.race([
              targetComponent.playWithBrowserPlayer(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('전체 재생 타임아웃')), 45000))
            ]);
            console.log('브라우저 플레이어 자동 재생 완료');
          } catch (playerError) {
            console.warn('브라우저 플레이어 재생 실패, 백그라운드로 대체:', playerError.message);
            // 재생 실패 시 fallback으로 백그라운드 재생 사용하지 않음 (사용자가 수동으로 재생할 수 있음)
            console.log('사용자가 수동으로 플레이어를 조작할 수 있습니다.');
          }
        } else {
          console.log('브라우저 플레이어를 찾을 수 없어 백그라운드 재생으로 대체');
          await this.playTranslatedAudioBackground();
        }
      } catch (error) {
        console.error('음성 재생 실패:', error);
        this.$vs.notify({
          title: '재생 오류',
          text: error.message || '음성 파일을 재생할 수 없습니다.',
          color: 'danger'
        });
        throw error;
      }
    },

    // 백그라운드 음성 재생 (fallback)
    async playTranslatedAudioBackground() {
      return new Promise(async (resolve, reject) => {
        try {
          let audioSrc = this.translatedAudioUrl;

          // 기존 Blob URL이 있으면 정리
          if (this.translatedAudioBlob) {
            URL.revokeObjectURL(this.translatedAudioBlob);
            this.translatedAudioBlob = null;
          }

          // Authorization이 필요한 URL인지 확인 (API 엔드포인트 패턴 체크)
          if (audioSrc.includes('/api/v1/file')) {
            try {
              const { audioBlob, blobUrl } = await this.downloadAudioWithAuth(audioSrc);
              audioSrc = blobUrl;
              this.translatedAudioBlob = blobUrl; // 나중에 정리를 위해 저장
            } catch (downloadError) {
              console.warn('인증된 다운로드 실패, 직접 URL 시도:', downloadError.message);
            }
          }

          const audio = new Audio(audioSrc);

          audio.onended = () => {
            console.log('백그라운드 음성 재생 완료');
            resolve();
          };

          audio.onerror = (error) => {
            console.error('백그라운드 음성 재생 실패:', error);
            reject(error);
          };

          audio.play().catch(reject);
        } catch (error) {
          console.error('백그라운드 음성 재생 준비 실패:', error);
          reject(error);
        }
      });
    }
  }
}
</script>

<style scoped lang="scss">

</style>
